<template>
  <div :key="resetKey" class="products" :class="settings.class">
    <div v-for="product of products" :key="product.sku" class="product-wrapper">
      <div class="product">
        <ImprovedProductImage
          v-for="image in getImages(product.sku)"
          :key="image.name"
          class="p-2"
          :width="settings.imageSize"
          :height="settings.imageSize"
          :src="image.url"
          loading="eager"
          square
          :fallback-img="null"
          @load="loadedImgs++"
          @error.once="handleError($event, image.name)"
        />
        <div class="info">
          <p>
            <span>{{ product.sku }}</span>
            <span v-if="settings.remark" class="float-right">{{ getProductAttributeValuesString(product, 'remark') }}</span>
          </p>
          <h6 class="title">
            {{ product.name }}
          </h6>
          <p v-if="settings.description">
            {{ product.description }}
          </p>
          <p v-if="settings.infoText">
            {{ product.infoText }}
          </p>

          <p v-if="store.dataCulture?.country === 'dk'">
            {{ getExtras(product) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useTools } from '@/composables/useTools'
import { useTranslation } from '@/composables/useTranslation'
import Message from '@/models/Message'
import { SimplifiedProduct } from '@/models/Product'
import DataService from '@/services/DataService'
import { useDefaultStore } from '@/store'
import { getImageUrl, getProductAttributeValuesString } from '@/utils/productUtils'
import { capitalize, computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import ImprovedProductImage from '@/components/products/ImprovedProductImage.vue'

const props = withDefaults(
  defineProps<{
    triggerPrint?: boolean
    total?: number
    preview?: boolean
  }>(),
  { total: 0 },
)
const emit = defineEmits(['update:triggerPrint', 'load'])

const Settings = {
  default: {
    nrOfImages: 2,
    description: true,
    infoText: true,
    // imageSize: '178px',
    imageSize: '162px',
    remark: true,
    class: '',
  },

  matrix: {
    nrOfImages: 1,
    description: false,
    infoText: false,
    imageSize: '75px',
    remark: false,
    class: 'matrix',
  },
}
const settings = process.env.NODE_ENV === 'development' ? Settings.default : Settings.default

const { setTempImageUrl } = useTools()
const store = useDefaultStore()
const { translate } = useTranslation()
const route = useRoute()

const products = ref<SimplifiedProduct[]>([])
const loadedImgs = ref(0)
const loadingErrors = ref(0)
const imgsLoaded = ref(false)
const resetKey = ref(0)

const loadingCount = computed(() => [loadedImgs.value, loadingErrors.value])

watch(loadingCount, () => {
  if (loadedImgs.value + loadingErrors.value === products.value.length * settings.nrOfImages) {
    imgsLoaded.value = true
    if (!props.preview) {
      emit('load')
      nextTick(() => {
        const title = document.title
        document.title = 'Burde Produktblad'
        window.print()
        document.title = title
        emit('update:triggerPrint', false)
      })
    }
  }
})

onMounted(() => {
  if (props.preview) {
    load(3)
  } else {
    const productsWarningAmount = store.settings?.app.imgFetchLimit || 200

    // if (this.total < productsWarningAmount || confirm(`Du försöker hämta ${this.total} artiklar. Vill du fortsätta?`)) {
    if (props.total < productsWarningAmount) {
      load()
    } else {
      store.addMessage(
        new Message('För många artiklar', `Du kan inte skriva ut en presentation för fler än ${productsWarningAmount} artiklar i nuläget`, 'warning'),
      )
      emit('update:triggerPrint', false)
    }
  }
})

const getImages = (sku: string) => {
  return [`${sku}.jpg`, `${sku}-up1.jpg`]
    .map((img) => ({
      name: img,
      url: getImageUrl(img, 256),
    }))
    .slice(0, settings.nrOfImages)
}

const handleError = async (e: Event, imageName: string) => {
  const target = e.target as HTMLImageElement
  ;(await setTempImageUrl(target, imageName, 256, 'DEMO')) || loadingErrors.value++
}
const load = async (limit = -1) => {
  // this.resetKey = 0
  // this.loadedImgs = 0
  // this.loadingErrors = 0

  const response = await DataService.getProducts({
    ...route.query,
    from: 0,
    to: limit,
  })

  if (response.data) {
    products.value = response.data
  }
}

const getExtras = (product: SimplifiedProduct) => {
  const minSalesPack = getProductAttributeValuesString(product, 'min sales pack')

  const extras = [
    {
      title: translate('recommended price', 'presentations', 'lowercase'),
      prefix: store.dataCulture ? store.availableCulturesByCountry.get(store.dataCulture?.country)?.currencySymbol + '.' : undefined,
      value: product.recommendedPriceInclVAT ? String(product.recommendedPriceInclVAT).replaceAll('.', ',') : undefined,
    },
    {
      title: translate('min sales pack', 'attributes'),
      value: minSalesPack,
    },
  ]
    .filter((o) => o.value)
    .map((o) => `${o.title}: ${o.prefix || ''} ${o.value}`)
    .join(', ')

  return capitalize(extras)
}
</script>

<style lang="scss" scoped>
.products {
  font-family: Quicksand;
}

.matrix {
  columns: 5;
  // display: flex;
  // flex: 0 0 20%;
  // flex-wrap: wrap;
}

.product-wrapper {
  // necessary to avoid flex page break bug
  break-inside: avoid;
  padding: 1.3em 0;
  font-size: 0.8em;
}

.product {
  display: flex;

  > * {
    padding: 0.5em;
  }

  .info {
    p {
      margin-bottom: 0.5em;
    }

    .title {
      font-weight: bold;
      word-break: break-all;
      font-size: 1em;
    }
  }
}
</style>
